import type { AssetTypes, EditAssetWorkflowOptions } from '../types';
import { AssetWorkflow } from '../AssetWorkflow';

export const generateAssetStatusWorkflow = (assetType: AssetTypes, options?: { deleteAction?: boolean }) => {
  const displayName = AssetWorkflow.getDisplayNameForAssetType(assetType);
  // When deleteAction is set, this is a real DELETE (the confirm step just gates it) rather than
  // the default archive-via-PUT. Only opt specific asset types in once their backend supports a
  // true delete endpoint; everything else keeps the archive behavior unchanged.
  const isDeleteAction = options?.deleteAction === true;
  const deactivateDefinition: EditAssetWorkflowOptions = {
    workflowKey: `deactivate${assetType}`,
    assetType,
    workflowIndex: 3,
    workflowAssets: [assetType],
    workflowType: 'editAssetWorkflow',
    deleteAction: isDeleteAction,
    workflowName: `Delete`,
    shipiumOnly: true,
    workflowDescription: `Delete ${displayName}`,
    // Archive mode derives `active` from the chosen `archived` value. A real delete ignores the
    // payload entirely, so leave the transform off (defaults to identity) — otherwise it would
    // dereference an undefined payload (the confirm step maps nothing).
    ...(isDeleteAction
      ? {}
      : {
          workflowStateTransformFunction: (assetPayload: any) => {
            return {
              ...assetPayload,
              active: assetPayload.archived ? false : true,
            };
          },
        }),
    assetSchemaMatcher: {
      type: 'object',
      properties: {
        active: { type: 'boolean', const: true },
        archived: { type: 'boolean', const: false },
      },
    },
    workflowActionIcon: 'DeleteOutlined',
    workflowStepOptions: [
      {
        workflowStepKey: `deactivate${assetType}`,
        workflowStepNavigationName: `Deactivate ${displayName}`,
        workflowComponent: {
          workflowComponentType: 'singleSelect',
          prompt: `Delete this ${displayName}? This action is not reversible!`,
          description: `Delete this ${displayName.toLowerCase()}. This will affect carrier selection!`,
          help: `You can use delete to remove configurations that will no longer be relevant to your organization.`,
          options: [
            { optionText: 'Yes Delete', optionValue: true },
            { optionText: 'No leave active', optionValue: false },
          ],
        },
        workflowStateToAssetMapping: isDeleteAction
          ? { mappingType: 'ignore' }
          : {
              mappingType: 'property',
              property: 'archived',
              excludeFromInitalState: true,
            },
      },
      {
        workflowStepKey: 'summary',
        workflowStepNavigationName: 'Configuration Summary',
        workflowComponent: {
          workflowComponentType: 'workflowSummary',
          title: `Activate ${displayName.toLowerCase()}`,
          description: '',
          help: '',
        },
        workflowStateToAssetMapping: {
          mappingType: 'ignore',
        },
      },
    ],
  };
  return { deactivateDefinition };
};
